import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AppConfiguration, AppConfigurationVersion, VersionChange, VersionChangeDetails } from "@/types/application";

export const useConfigurationVersions = (configurationId: string) => {
  const queryClient = useQueryClient();

  // Fetch all versions for a configuration
  const { data: versions, isLoading } = useQuery({
    queryKey: ['config-versions', configurationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_configuration_versions')
        .select('*')
        .eq('configuration_id', configurationId)
        .order('version_number', { ascending: false });

      if (error) throw error;

      return (data || []).map(v => ({
        id: v.id,
        configurationId: v.configuration_id,
        versionNumber: v.version_number,
        name: v.name,
        description: v.description,
        configurationData: v.configuration_data as any,
        customers: v.customers || [],
        isActive: v.is_active,
        changeSummary: v.change_summary,
        changeDetails: v.change_details as any,
        createdBy: v.created_by,
        createdAt: v.created_at,
        versionLabel: v.version_label,
        isMilestone: v.is_milestone,
      })) as AppConfigurationVersion[];
    },
    enabled: !!configurationId
  });

  // Generate change details by comparing configurations
  const generateChangeDetails = (
    oldConfig: Partial<AppConfiguration>,
    newConfig: Partial<AppConfiguration>
  ): VersionChangeDetails => {
    const changes: VersionChange[] = [];

    // Compare name
    if (oldConfig.name !== newConfig.name) {
      changes.push({
        field: 'name',
        oldValue: oldConfig.name,
        newValue: newConfig.name
      });
    }

    // Compare description
    if (oldConfig.description !== newConfig.description) {
      changes.push({
        field: 'description',
        oldValue: oldConfig.description,
        newValue: newConfig.description
      });
    }

    // Compare keys
    const oldKeys = oldConfig.configurationData?.keys || [];
    const newKeys = newConfig.configurationData?.keys || [];

    // Detect added keys
    newKeys.forEach(newKey => {
      const oldKey = oldKeys.find(k => k.key === newKey.key);
      if (!oldKey) {
        changes.push({
          field: 'keys',
          action: 'added',
          key: newKey.key,
          value: newKey.value
        });
      } else if (JSON.stringify(oldKey) !== JSON.stringify(newKey)) {
        changes.push({
          field: 'keys',
          action: 'modified',
          key: newKey.key,
          oldValue: oldKey.value,
          newValue: newKey.value
        });
      }
    });

    // Detect removed keys
    oldKeys.forEach(oldKey => {
      const newKey = newKeys.find(k => k.key === oldKey.key);
      if (!newKey) {
        changes.push({
          field: 'keys',
          action: 'removed',
          key: oldKey.key,
          oldValue: oldKey.value
        });
      }
    });

    // Compare customers
    const oldCustomers = oldConfig.customers || [];
    const newCustomers = newConfig.customers || [];
    if (JSON.stringify(oldCustomers.sort()) !== JSON.stringify(newCustomers.sort())) {
      changes.push({
        field: 'customers',
        oldValue: oldCustomers,
        newValue: newCustomers
      });
    }

    // Compare isActive
    if (oldConfig.isActive !== newConfig.isActive) {
      changes.push({
        field: 'isActive',
        oldValue: oldConfig.isActive,
        newValue: newConfig.isActive
      });
    }

    const summary = generateChangeSummary(changes);
    return { changes, summary };
  };

  const generateChangeSummary = (changes: VersionChange[]): string => {
    const parts: string[] = [];

    changes.forEach(change => {
      if (change.field === 'keys') {
        if (change.action === 'added') {
          parts.push(`Added key: ${change.key}`);
        } else if (change.action === 'modified') {
          parts.push(`Modified key: ${change.key}`);
        } else if (change.action === 'removed') {
          parts.push(`Removed key: ${change.key}`);
        }
      } else if (change.field === 'name') {
        parts.push('Changed name');
      } else if (change.field === 'description') {
        parts.push('Updated description');
      } else if (change.field === 'customers') {
        const added = (change.newValue as string[]).filter(c => !(change.oldValue as string[]).includes(c));
        const removed = (change.oldValue as string[]).filter(c => !(change.newValue as string[]).includes(c));
        if (added.length > 0) parts.push(`Added ${added.length} customer(s)`);
        if (removed.length > 0) parts.push(`Removed ${removed.length} customer(s)`);
      } else if (change.field === 'isActive') {
        parts.push(change.newValue ? 'Activated' : 'Deactivated');
      }
    });

    return parts.join(', ') || 'No changes detected';
  };

  // Rollback to a specific version
  const rollbackToVersion = async (versionId: string) => {
    const version = versions?.find(v => v.id === versionId);
    if (!version) throw new Error('Version not found');

    const { error } = await supabase
      .from('app_configurations')
      .update({
        name: version.name,
        description: version.description,
        configuration_data: version.configurationData as any,
        customers: version.customers,
        is_active: version.isActive,
        updated_at: new Date().toISOString()
      })
      .eq('id', configurationId);

    if (error) throw error;

    queryClient.invalidateQueries({ queryKey: ['app-configurations'] });
    queryClient.invalidateQueries({ queryKey: ['config-versions'] });
    toast.success(`Rolled back to version ${version.versionNumber}`);
  };

  // Label a version
  const labelVersion = async (versionId: string, label: string, isMilestone: boolean = false) => {
    const { error } = await supabase
      .from('app_configuration_versions')
      .update({
        version_label: label,
        is_milestone: isMilestone
      })
      .eq('id', versionId);

    if (error) throw error;

    queryClient.invalidateQueries({ queryKey: ['config-versions'] });
    toast.success('Version labeled successfully');
  };

  // Compare two versions
  const compareVersions = (versionId1: string, versionId2: string) => {
    const version1 = versions?.find(v => v.id === versionId1);
    const version2 = versions?.find(v => v.id === versionId2);

    if (!version1 || !version2) return null;

    return generateChangeDetails(version1, version2);
  };

  return {
    versions: versions || [],
    isLoading,
    rollbackToVersion,
    labelVersion,
    compareVersions,
    generateChangeDetails
  };
};
